import type { Board } from "../board";
import { CollectableItem, type CollectablePickupResult, type UseItemResult } from "../collectableItem";
import { Event } from "../events/event";
import { EventParameter } from "../events/eventParameter";
import { EventType } from "../events/eventType";
import type { Player } from "../player";
import { Strings } from "../strings";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import { type Toggleable } from "../interfaces";

export class NightVisionEquipment extends CollectableItem implements Toggleable {
	static key = "NightVisionEquipment";
	enabled: boolean = false;

	constructor(count: number = 1, enabled: boolean = false) {
		super(count);
		this.enabled = enabled;
	}

	pickupBy(player: Player, board: Board): CollectablePickupResult {
		const existing = player.inventory.getItemOfClass(NightVisionEquipment);
		if (!existing) {
			this.enabled = true;
			player.inventory.addItem(this);
			board.stats.recordItemCollected(NightVisionEquipment.key, this.count);
		}

		const event = new Event(EventType.GOT_ITEM);
		event.setParameter(EventParameter.AMMO_AMOUNT, String(this.count));
		event.setParameter(EventParameter.AMMO_NAME, this.getNameKey());
		board.addEvent(event);

		return { pickupKey: NightVisionEquipment.key, amount: this.count, isInfinite: false };
	}

	isUsable(): boolean {
		return true;
	}

	getStateLabel(): string {
		return Strings.get(this.enabled ? "ui_itemStateOn" : "ui_itemStateOff") ?? (this.enabled ? "ON" : "OFF");
	}

	getUseButtonLabel(): string {
		return Strings.get(this.enabled ? "ui_itemDisable" : "ui_itemEnable") ?? (this.enabled ? "DISABLE" : "ENABLE");
	}

	use(_player: Player, board: Board): UseItemResult {
		this.enabled = !this.enabled;
		board.addNonPhysicalThing(new Sound(SoundId.NIGHT_VISION_TOGGLE));
		return { consumed: false, turnPassed: true };
	}

	serialize(): Record<string, unknown> {
		return { ...super.serialize(), enabled: this.enabled };
	}

	static deserialize(data: any): NightVisionEquipment {
		return new NightVisionEquipment(data.count, data.enabled ?? false);
	}
}
